"use client";

import React, { useEffect } from "react";
import API_URL from "@packages/lib/server-actions/api-urls";
import makeApiCall from "@packages/lib/server-actions/rest-api";

// Shared error logging function
const logError = async (errorData: Record<string, unknown>) => {
  const isLocalhost =
    typeof window !== "undefined" && window.location.hostname === "localhost";

  if (!isLocalhost && process.env.NEXT_PUBLIC_ENVIRONMENT != "local") {
    try {
      await makeApiCall({
        url: `${process.env.NEXT_PUBLIC_BFF_API_DOMAIN}${API_URL?.logErrors}`,
        method: "POST",
        body: errorData,
        headers: {
          "x-api-key": `${process.env.NEXT_PUBLIC_X_API_KEY}`,
        },
      });
    } catch (err) {
      console.error("Failed to log error:", err);
    }
  }
};

// Shared error data creation function
const createErrorData = (type: string, message: string, stack?: string) => {
  const now = new Date();
  return {
    type,
    date: now.toISOString().split("T")[0],
    time: now.toTimeString().split(" ")[0],
    componentName: extractComponentNameFromStack(stack),
    errorMessage: message,
    url: window.location.href,
    path: window.location.pathname,
    projectName: process.env.PROJECT,
    environment: process.env.ENVIRONMENT,
    affiliateId: process.env.AFFILATE_ID,
    sitecode: process.env.SITE_CODE,
  };
};

// Shared component name extraction function
function extractComponentNameFromStack(
  stack: string | undefined,
): string | null {
  if (!stack) return null;
  const match = stack.match(/at\s+([A-Za-z0-9_$.]+)\s*\(/);
  return match ? match[1] : null;
}

// Global error handler hook
function useGlobalErrorHandler() {
  useEffect(() => {
    const maximum_retries = 1;
    const chunkname = "chunkLoadErrorRetries";

    const reloadIfChunkFails = () => {
      const retries = parseInt(sessionStorage.getItem(chunkname) || "0", 3);

      if (retries < maximum_retries) {
        sessionStorage.setItem(chunkname, String(retries + 1));
        console.warn("Chunk load failed, reloading...");
        window.location.reload();
      } else {
        console.error("Chunk load failed again — not reloading to avoid loop.");
      }
    };

    const isCodeRelatedError = (
      filename: string,
      stack: string,
      message: string,
    ) => {
      // Skip third-party and browser errors
      const skipPatterns = [
        "chrome-extension://",
        "moz-extension://",
        "safari-extension://",
        "Script error",
        "Non-Error promise rejection captured",
        "ResizeObserver loop limit exceeded",
        "Network request failed",
        "Failed to fetch",
      ];

      if (
        skipPatterns.some(
          (pattern) => message.includes(pattern) || filename.includes(pattern),
        )
      ) {
        return false;
      }

      // Only log errors from your project files
      const projectPatterns = [
        "/apps/",
        "/packages/",
        "/src/",
        "localhost",
        window.location.origin,
      ];

      return projectPatterns.some(
        (pattern) =>
          filename.includes(pattern) ||
          stack.includes(pattern) ||
          filename.startsWith(window.location.origin),
      );
    };

    const handleError = (e: ErrorEvent) => {
      if (e.message?.includes("Loading chunk")) {
        reloadIfChunkFails();
      }

      // Only log code-related errors
      if (
        isCodeRelatedError(
          e.filename || "",
          e.error?.stack || "",
          e.message || "",
        )
      ) {
        const errorData = createErrorData(
          "javascript_error",
          e.message || "Unknown error",
          e.error?.stack,
        );
        logError(errorData);
      }
    };

    const handleRejection = (e: PromiseRejectionEvent) => {
      if (e.reason?.message?.includes("Loading chunk")) {
        reloadIfChunkFails();
      }

      // Only log code-related promise rejections
      const message =
        e.reason?.message ||
        e.reason?.toString() ||
        "Unhandled promise rejection";
      const stack = e.reason?.stack || "";

      if (isCodeRelatedError("", stack, message)) {
        const errorData = createErrorData("promise_rejection", message, stack);
        logError(errorData);
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
  }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    let type = "unknown_error";
    if (error.name === "TypeError") {
      type = "type_error";
    } else if (error.name === "ReferenceError") {
      type = "reference_error";
    } else if (error.message?.includes("Cannot read properties of")) {
      type = "null_or_undefined_access";
    } else if (error.message?.includes("Loading chunk")) {
      type = "chunk_load_error";
    } else if (errorInfo?.componentStack) {
      type = "react_rendering_error";
    }

    const errorData = createErrorData(type, error.message, error?.stack);
    logError(errorData);
    this.setState({ error, errorInfo: errorInfo });
  }

  render() {
    const { hasError, error, errorInfo } = this.state;

    if (hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1
              className={`text-4xl font-bold text-blue-500
               animate-bounce`}
            >
              Oops! Something went wrong. <br />A client side error occurred
            </h1>
            {error?.message && (
              <p className="mt-4 text-lg text-red-600">
                Error: {error.message}
              </p>
            )}
            <div className="mt-2 text-gray-500">
              <p>
                Please try refreshing the page or contact support if the issue
                persists.
              </p>
              <p>Environment: {process.env.ENVIRONMENT ?? "local"}</p>
              <p>{process.env.PROJECT}</p>
              <p>Pathname: {window?.location.pathname}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 px-6 py-3 text-lg font-medium text-white bg-sky-500 rounded-lg shadow-md hover:bg-sky-600 active:scale-95 transition-transform duration-150"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return <GlobalErrorWrapper>{this.props.children}</GlobalErrorWrapper>;
  }
}

// Wrapper component to handle all errors
function GlobalErrorWrapper({ children }: { children: React.ReactNode }) {
  useGlobalErrorHandler();
  return <>{children}</>;
}

export default ErrorBoundary;
